import { createColumnHelper } from '@tanstack/react-table';
import { formatInTimeZone } from 'date-fns-tz';
import {
  COLUMN_DEFAULT_SIZE_BASE,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import {
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_DEFAULT_SIZE_XL,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { ReportMetricsResponse } from 'src/contexts/ReportMetricsContext/ReportMetricsContextV2';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useReportV2ColumnPrecisionByColumnId } from 'src/hooks/useColumnUserSetting';
import { FormatOption } from 'src/modals/EditTableColumns';
import { defaultColumnHelperAccessor } from 'src/tables/ReportTableCommon/DefaultColumnHelperAccessor';
import { LinkCell } from 'src/tables/ReportTableCommon/LinkCell';
import { SHIMMERING_DIV_HEIGHT_REPORT } from 'src/tables/ReportTableCommon/ReportTableCommon.constants';
import { reportV2AccessorFn } from 'src/tables/ReportTableCommon/ReportTableCommon.utils';
import { TextCell } from 'src/tables/ReportTableCommon/TextCell';
import { TableShimmeringDiv } from 'src/tables/Table/Table.styled';
import { DateCell, InHandCell } from 'src/tables/Table/TableCells';
import { ReportColumnPersonalizationFlags } from 'src/utils/columns/columnUtils.types';
import {
  SoldTicketReportTableGroupingColumnId,
  SoldTicketReportTableValueColumnId,
} from 'src/utils/columns/soldTicket/soldTicketColumnUtils.types';
import {
  SOLD_TICKET_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID,
  SOLD_TICKET_REPORT_TABLE_VALUE_COLUMN_ID_TO_CONTENT_ID,
} from 'src/utils/constants/contentIdMaps';
import { getListingDetailsRelativeUrl } from 'src/utils/inventoryUtils';
import { getPurchaseOrderRelativeUrl } from 'src/utils/purchaseUtils';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import {
  createSaleEventPageUrl,
  getSaleDetailsRelativeUrl,
} from 'src/utils/saleUtils';
import { PointOfSaleSaleStatus, ReportValueType } from 'src/WebApiController';

const columnHelper = createColumnHelper<ReportMetricsResponse | null>();

export const SOLD_TICKET_REPORT_SPECIAL_TABLE_COLUMNS_CONFIG = {
  [SoldTicketReportTableGroupingColumnId.EventDateLocal]: columnHelper.accessor(
    reportV2AccessorFn(SoldTicketReportTableGroupingColumnId.EventDateLocal),
    {
      id: SoldTicketReportTableGroupingColumnId.EventDateLocal,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              SOLD_TICKET_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                SoldTicketReportTableGroupingColumnId.EventDateLocal
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_XL,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({
        getValue,
        row: { original },
        column: { id: columnId },
      }) {
        const value = getValue<string>();

        const { userDefinedPrecision = FormatOption.DateTime_MMDD } =
          useReportV2ColumnPrecisionByColumnId(
            columnId,
            ReportTypesV2.SoldTicket
          );

        if (!original) {
          return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
        }

        const dateValue = value ? new Date(value).toISOString() : value;

        return (
          <DateCell
            precision={userDefinedPrecision}
            date={dateValue}
            align="left"
            enableUtcFallback
            hideRelativeTerms
            useSiteTimeZone={false}
          />
        );
      },
    }
  ),
  [SoldTicketReportTableGroupingColumnId.SaleId]: columnHelper.accessor(
    reportV2AccessorFn(SoldTicketReportTableGroupingColumnId.SaleId),
    {
      id: SoldTicketReportTableGroupingColumnId.SaleId,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              SOLD_TICKET_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                SoldTicketReportTableGroupingColumnId.SaleId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: ({ getValue }) => {
        const saleId = getValue<string | null>();

        if (!saleId) {
          return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
        }

        const saleIds = [Number(saleId)];

        return (
          <LinkCell
            text={saleIds.map((saleId) => `#${saleId}`)}
            url={saleIds.map((saleId) => getSaleDetailsRelativeUrl(saleId))}
            separator={<span>{', '}</span>}
            openInNewTab
            align="left"
            style={{ fontWeight: 600 }}
          />
        );
      },
    }
  ),
  [SoldTicketReportTableGroupingColumnId.ListingId]: columnHelper.accessor(
    reportV2AccessorFn(SoldTicketReportTableGroupingColumnId.ListingId),
    {
      id: SoldTicketReportTableGroupingColumnId.ListingId,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              SOLD_TICKET_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                SoldTicketReportTableGroupingColumnId.ListingId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: ({ getValue }) => {
        const listingId = getValue<string | null>();

        if (!listingId) {
          return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
        }

        const listingIds = [Number(listingId)];

        return (
          <LinkCell
            text={listingIds.map((listingId) => `#${listingId}`)}
            url={listingIds.map((listingId) =>
              getListingDetailsRelativeUrl(listingId)
            )}
            separator={<span>{', '}</span>}
            openInNewTab
            align="left"
            style={{ fontWeight: 600 }}
          />
        );
      },
    }
  ),
  [SoldTicketReportTableGroupingColumnId.PurchaseOrderId]:
    columnHelper.accessor(
      reportV2AccessorFn(SoldTicketReportTableGroupingColumnId.PurchaseOrderId),
      {
        id: SoldTicketReportTableGroupingColumnId.PurchaseOrderId,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                SOLD_TICKET_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  SoldTicketReportTableGroupingColumnId.PurchaseOrderId
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: ({ getValue }) => {
          const poId = getValue<string | null>();

          if (!poId) {
            return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
          }

          const poIds = [Number(poId)];

          return (
            <LinkCell
              text={poIds.map((poId) => `#${poId}`)}
              url={poIds.map((poId) => getPurchaseOrderRelativeUrl(poId))}
              separator={<span>{', '}</span>}
              openInNewTab
              align="left"
              style={{ fontWeight: 600 }}
            />
          );
        },
      }
    ),
  [SoldTicketReportTableGroupingColumnId.EventId]: columnHelper.accessor(
    reportV2AccessorFn(SoldTicketReportTableGroupingColumnId.EventId),
    {
      id: SoldTicketReportTableGroupingColumnId.EventId,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              SOLD_TICKET_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                SoldTicketReportTableGroupingColumnId.EventId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: (params) => {
        const eventId = params.getValue<number | null>();
        if (!eventId) {
          return <TextCell {...params} align={'left'} />;
        }
        const eventUrl = createSaleEventPageUrl(eventId.toString());

        return (
          <LinkCell
            text={String(params.getValue())}
            url={eventUrl}
            align="left"
          />
        );
      },
    }
  ),
  [SoldTicketReportTableGroupingColumnId.InHandDate]: columnHelper.accessor(
    reportV2AccessorFn(SoldTicketReportTableGroupingColumnId.InHandDate),
    {
      id: SoldTicketReportTableGroupingColumnId.InHandDate,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              SOLD_TICKET_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                SoldTicketReportTableGroupingColumnId.InHandDate
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: ({ getValue, row: { original } }) => {
        const value = getValue<string | null>();
        if (!original) {
          return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
        }
        return (
          <TableCellDiv align="left">
            <InHandCell
              date={value ? new Date(value) : null}
              needsToFulfill={Boolean(
                original?.['SaleStatusName'] &&
                  ![
                    PointOfSaleSaleStatus.ProcessingFulfillment,
                    PointOfSaleSaleStatus.Fulfilled,
                    PointOfSaleSaleStatus.CancelledHold,
                    PointOfSaleSaleStatus.Rejected,
                    PointOfSaleSaleStatus.PendingRejection,
                  ].includes(original?.['SaleStatusName'])
              )}
              handleFulfillmentState
            />
          </TableCellDiv>
        );
      },
      sortingFn: 'datetime',
    }
  ),
  [SoldTicketReportTableGroupingColumnId.SaleMonth]: columnHelper.accessor(
    reportV2AccessorFn(SoldTicketReportTableGroupingColumnId.SaleMonth),
    {
      id: SoldTicketReportTableGroupingColumnId.SaleMonth,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              SOLD_TICKET_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                SoldTicketReportTableGroupingColumnId.SaleMonth
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_BASE,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original } }) {
        if (!original) {
          return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
        }

        const dateValue = getValue<string | null | undefined>();

        return (
          <TableCellDiv align="left">
            <div>
              {/* Already converted to account timezone */}
              {dateValue
                ? formatInTimeZone(new Date(dateValue), 'UTC', 'MMMM yyyy')
                : ''}
            </div>
          </TableCellDiv>
        );
      },
    }
  ),
  [SoldTicketReportTableGroupingColumnId.SaleWeek]: columnHelper.accessor(
    reportV2AccessorFn(SoldTicketReportTableGroupingColumnId.SaleWeek),
    {
      id: SoldTicketReportTableGroupingColumnId.SaleWeek,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              SOLD_TICKET_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                SoldTicketReportTableGroupingColumnId.SaleWeek
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original } }) {
        if (!original) {
          return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
        }

        const dateValue = getValue<string | null | undefined>();

        const dateEndOfWeek = dateValue ? new Date(dateValue) : null;
        if (dateEndOfWeek) {
          dateEndOfWeek.setDate(dateEndOfWeek.getDate() + 6);
        }

        return (
          <TableCellDiv align="left">
            <div>
              {/* Already converted to account timezone */}
              {dateValue && dateEndOfWeek
                ? formatInTimeZone(new Date(dateValue), 'UTC', 'yyyy/MM/dd') +
                  ' - ' +
                  formatInTimeZone(dateEndOfWeek, 'UTC', 'yyyy/MM/dd')
                : ''}
            </div>
          </TableCellDiv>
        );
      },
    }
  ),
  [SoldTicketReportTableGroupingColumnId.SaleDay]: columnHelper.accessor(
    reportV2AccessorFn(SoldTicketReportTableGroupingColumnId.SaleDay),
    {
      id: SoldTicketReportTableGroupingColumnId.SaleDay,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              SOLD_TICKET_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                SoldTicketReportTableGroupingColumnId.SaleDay
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original } }) {
        if (!original) {
          return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
        }

        const dateValue = getValue<string | null | undefined>();

        return (
          <TableCellDiv align="left">
            <div>
              {/* Already converted to account timezone */}
              {dateValue
                ? formatInTimeZone(new Date(dateValue), 'UTC', 'yyyy/MM/dd')
                : ''}
            </div>
          </TableCellDiv>
        );
      },
    }
  ),
} as const;

export const soldTicketReportTableColumnDef = (
  columnId: string,
  columnValue: string,
  valueType: ReportValueType | undefined,
  personalizationFlags: Partial<ReportColumnPersonalizationFlags> | undefined
) => {
  const specialConfig = Object.entries(
    SOLD_TICKET_REPORT_SPECIAL_TABLE_COLUMNS_CONFIG
  ).find(([key]) => key === columnId)?.[1];

  if (specialConfig) {
    return specialConfig;
  }

  const headerContentId =
    SOLD_TICKET_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
      columnId as unknown as SoldTicketReportTableGroupingColumnId
    ] ||
    SOLD_TICKET_REPORT_TABLE_VALUE_COLUMN_ID_TO_CONTENT_ID[
      columnId as unknown as SoldTicketReportTableValueColumnId
    ];

  return defaultColumnHelperAccessor(
    columnHelper,
    columnId,
    columnValue,
    headerContentId,
    ReportTypesV2.SoldTicket,
    valueType,
    personalizationFlags
  );
};
